import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type {
  Adresse,
  Bilingue,
  CleModele,
  Devise,
  FormatPage,
  Langue,
  ModeReglement,
  RegimeTva,
} from '@artosera/shared';
import { api, requeteApi, requeteTelechargement, type AppelApi } from './client.js';
import type { Pagination } from './types.js';
import { appelDeRequete, cleMetier, dansLaPortee, precedentDansLaPortee } from './cles.js';

/**
 * Ventes — `/v1/g/:slug/ventes` (`docs/dev/api.md` § 17, S4-a). Les formes
 * suivent `apps/api/src/ventes/presentation.ts`, `profil-fiscal.ts` et
 * `modeles.ts` au champ près : un seul type ici, jamais un par écran (même
 * parti pris que `api/oeuvres.ts` et `api/contacts.ts`).
 *
 * Les RÉSERVATIONS n'y sont pas : elles vivent dans `api/reservations.ts`
 * (§ 14.6, le carnet). Une réservation qui se clôt en vente passe en
 * `transformee` côté serveur, sans qu'aucune route de ce module y touche.
 */

export type TypeFacture = 'facture' | 'avoir';
export const TYPES_FACTURE_UI: readonly TypeFacture[] = ['facture', 'avoir'];

export type EtatFacture = 'brouillon' | 'emise' | 'payee' | 'annulee';
export const ETATS_FACTURE_UI: readonly EtatFacture[] = ['brouillon', 'emise', 'payee', 'annulee'];

/** Le cartel figé sur une ligne d'œuvre (7.6) : jamais relu depuis la fiche. */
export interface CartelLigneFacture {
  readonly artiste?: string;
  readonly titre?: string;
  readonly annee?: string;
  readonly technique?: string;
  readonly dimensions?: string;
  readonly numeroInventaire?: string;
  readonly edition?: string;
}

export interface LigneFacturePresentee {
  readonly designation: string;
  readonly quantite: number;
  readonly prixUnitaireCentimes: number;
  readonly remisePointsDeBase: number;
  readonly prixUnitaireNetCentimes: number;
  readonly regimeTva: RegimeTva;
  readonly motifExoneration: string | null;
  readonly droitDeSuite: boolean;
  /** Présents seulement pour qui voit les œuvres (5.1, le Comptable non). */
  readonly oeuvreId?: string | null;
  readonly exemplaireId?: string | null;
  readonly cartel?: CartelLigneFacture | null;
  /** Présent seulement pour qui a le droit `artworks:read-artist-price`. */
  readonly prixAchatCentimes?: number | null;
}

export interface TotalCategorieTva {
  readonly categorie: string;
  readonly regime: RegimeTva;
  readonly tauxPointsDeBase: number;
  readonly baseCentimes: number;
  readonly tvaCentimes: number;
}

export interface TotauxFacture {
  readonly totalHtCentimes: number;
  readonly totalTvaCentimes: number;
  readonly totalTtcCentimes: number;
  readonly droitDeSuiteCentimes: number;
  readonly parCategorieTva: readonly TotalCategorieTva[];
}

export interface FacturePresentee {
  readonly id: string;
  readonly numero: string | null;
  readonly type: TypeFacture;
  readonly etat: EtatFacture;
  readonly contactId: string;
  readonly contactNom?: string;
  readonly factureOrigineId: string | null;
  readonly lignes: readonly LigneFacturePresentee[];
  readonly remisePointsDeBase: number;
  readonly devise: Devise;
  readonly langue: Langue;
  readonly droitDeSuiteAChargeAcheteur: boolean;
  readonly mentions: readonly Bilingue[];
  readonly note: string | null;
  readonly reference: string | null;
  readonly modeReglement: ModeReglement;
  readonly conditionsReglement: string | null;
  readonly totaux: TotauxFacture;
  /** Vrai quand les totaux sont ceux FIGÉS à l'émission (7.6). */
  readonly totauxFiges: boolean;
  readonly emiseLe: string | null;
  readonly echeanceLe: string | null;
  readonly dateLivraison: string | null;
  readonly montantRegleCentimes: number;
  readonly resteADevoirCentimes: number;
  readonly pdfDisponible: boolean;
  readonly facturXDisponible: boolean;
  readonly creeLe: string;
  readonly modifieLe: string;
  /**
   * Version du brouillon : chaque modification l'incrémente. L'émission
   * renvoie celle que l'écran AFFICHE (`versionAttendue`) : un brouillon
   * modifié entre-temps par quelqu'un d'autre est refusé (409
   * `brouillon_modifie`) au lieu d'être émis tel que personne ne l'a relu.
   */
  readonly version: number;
}

export interface ReglementPresente {
  readonly id: string;
  readonly factureId: string;
  readonly factureNumero?: string | null;
  readonly montantCentimes: number;
  readonly devise: Devise;
  readonly mode: ModeReglement;
  readonly date: string;
  readonly reference: string | null;
  readonly note: string | null;
  readonly creeLe: string;
}

export interface AvoirRecent {
  readonly id: string;
  readonly numero: string | null;
  readonly emiseLe: string | null;
  readonly totalTtcCentimes: number;
}

export interface FicheFacture {
  readonly facture: FacturePresentee;
  readonly reglements: readonly ReglementPresente[];
  readonly avoirs: readonly AvoirRecent[];
  readonly oeuvresVisibles: boolean;
}

// ── Entrée d'API ─────────────────────────────────────────────────────────────

export interface CorpsLigneFacture {
  oeuvreId?: string;
  exemplaireId?: string;
  designation?: string;
  quantite?: number;
  prixUnitaireCentimes?: number;
  remisePointsDeBase?: number;
  regimeTva?: RegimeTva;
  motifExoneration?: string;
  prixAchatCentimes?: number;
  droitDeSuite?: boolean;
}

export interface CorpsFacture {
  contactId: string;
  lignes: CorpsLigneFacture[];
  remisePointsDeBase?: number;
  devise?: Devise;
  langue?: Langue;
  droitDeSuiteAChargeAcheteur?: boolean;
  note?: string;
  reference?: string;
  modeReglement?: ModeReglement;
  conditionsReglement?: string;
  echeanceLe?: string;
  dateLivraison?: string;
}

export type CorpsMiseAJourFacture = Partial<CorpsFacture>;

export interface FiltresFactures {
  etat?: readonly EtatFacture[];
  type?: TypeFacture;
  contactId?: string;
  annee?: number;
  du?: string;
  au?: string;
  impayees?: boolean;
  q?: string;
  tri?: 'recent' | 'ancien' | 'numero' | 'montant';
  page?: number;
  parPage?: number;
}

export interface ReponseListeFactures {
  readonly factures: readonly FacturePresentee[];
  readonly pagination: Pagination;
  readonly oeuvresVisibles: boolean;
}

export interface CorpsEmission {
  /** Date d'émission ; donne aussi l'année de la séquence (7.6). */
  emiseLe?: string;
  /**
   * La version du brouillon que l'utilisateur a vue et confirme (exigée,
   * ARTO-51) : l'émission est refusée (`409 brouillon_modifie`) si le
   * brouillon a changé depuis.
   */
  versionAttendue: number;
}

export interface ReponseEmission {
  readonly facture: FacturePresentee;
  readonly documentEnFile: boolean;
}

export interface CorpsAvoir {
  lignes?: readonly { rang: number; quantite?: number }[];
  motif?: string;
  emiseLe?: string;
  langue?: Langue;
}

export interface ReponseAvoir {
  readonly avoir: FacturePresentee;
  /** Vrai quand l'avoir reprend la facture entière (met la facture en `annulee`). */
  readonly total: boolean;
  readonly oeuvresLiberees: readonly string[];
  readonly documentEnFile: boolean;
}

export interface CorpsReglement {
  montantCentimes: number;
  mode: ModeReglement;
  date?: string;
  devise?: Devise;
  reference?: string;
  note?: string;
}

export interface ResumeFactureApresReglement {
  readonly id: string;
  readonly numero: string | null;
  readonly etat: EtatFacture;
  readonly montantRegleCentimes: number;
  readonly resteADevoirCentimes: number;
}

export interface ReponseReglement {
  readonly reglement: ReglementPresente;
  readonly facture: ResumeFactureApresReglement;
}

export interface ReponseSuppressionReglement {
  readonly efface: boolean;
  readonly facture: ResumeFactureApresReglement;
}

export interface FiltresJournalReglements {
  du?: string;
  au?: string;
  mode?: ModeReglement;
  factureId?: string;
  page?: number;
  parPage?: number;
}

export interface ReponseJournalReglements {
  readonly reglements: readonly ReglementPresente[];
  readonly totalCentimes: number;
  readonly pagination: Pagination;
}

export interface ReponsePdfFacture {
  readonly numero: string;
  readonly url: string;
  readonly expireLe: string;
  readonly expireDansSecondes: number;
}

// ── Profil fiscal (17.2) ─────────────────────────────────────────────────────

export interface TrancheDroitDeSuite {
  readonly plafondCentimes: number | null;
  readonly tauxPointsDeBase: number;
}

export interface BaremeDroitDeSuite {
  readonly seuilCentimes: number;
  readonly plafondCentimes: number;
  readonly tranches: readonly TrancheDroitDeSuite[];
}

export interface EmetteurFacture {
  readonly raisonSociale: string;
  readonly formeJuridique?: string;
  readonly capitalCentimes?: number;
  readonly siren?: string;
  readonly siret?: string;
  readonly tvaIntracom?: string;
  readonly rcs?: string;
  readonly codeApe?: string;
  readonly adresse: Adresse;
  readonly courriel?: string;
  readonly telephone?: string;
  readonly siteWeb?: string;
  readonly iban?: string;
  readonly bic?: string;
  readonly banque?: string;
  readonly mentionsComplementaires: readonly string[];
  readonly escompte: string | null;
}

export interface MentionsParRegime {
  normal?: Bilingue;
  reduit_5_5?: Bilingue;
  marge?: Bilingue;
  exonere?: Bilingue;
}

export interface ProfilFiscal {
  readonly assujetti: boolean;
  readonly franchiseEnBase: boolean;
  readonly enFranchise: boolean;
  readonly numeroTva: string | null;
  readonly regimeParDefaut: RegimeTva;
  readonly tauxNormalPointsDeBase: number;
  readonly tauxReduitPointsDeBase: number;
  readonly droitDeSuite: BaremeDroitDeSuite;
  readonly droitDeSuiteAChargeAcheteur: boolean;
  readonly droitDeSuiteBeneficiaire: string | null;
  readonly mentions: MentionsParRegime;
  readonly delaiPaiementJours: number;
  readonly emetteur: EmetteurFacture;
  readonly modifieLe: string;
}

export interface CorpsAdresseEmetteur {
  libelle?: string;
  ligne1: string;
  ligne2?: string;
  codePostal: string;
  ville: string;
  pays: string;
}

export interface CorpsEmetteur {
  raisonSociale: string;
  formeJuridique?: string;
  capitalCentimes?: number;
  siren?: string;
  siret?: string;
  tvaIntracom?: string;
  rcs?: string;
  codeApe?: string;
  adresse: CorpsAdresseEmetteur;
  courriel?: string;
  telephone?: string;
  siteWeb?: string;
  iban?: string;
  bic?: string;
  banque?: string;
  mentionsComplementaires?: string[];
  escompte?: string | null;
}

export interface CorpsMiseAJourProfil {
  assujetti?: boolean;
  franchiseEnBase?: boolean;
  numeroTva?: string;
  regimeParDefaut?: RegimeTva;
  tauxNormalPointsDeBase?: number;
  tauxReduitPointsDeBase?: number;
  droitDeSuite?: {
    seuilCentimes: number;
    plafondCentimes: number;
    tranches: { plafondCentimes: number | null; tauxPointsDeBase: number }[];
  };
  droitDeSuiteAChargeAcheteur?: boolean;
  droitDeSuiteBeneficiaire?: string;
  mentions?: MentionsParRegime;
  delaiPaiementJours?: number;
  emetteur?: CorpsEmetteur;
}

// ── Modèles de documents (17.3) ──────────────────────────────────────────────

export interface CouleursModele {
  readonly encre: string;
  readonly papier: string;
  readonly accent: string;
  readonly sourdine: string;
  readonly filet: string;
}

export type PoliceModele = 'serif' | 'sans' | 'mono';

export interface ModeleDocument {
  readonly cle: CleModele;
  readonly nom: string;
  readonly logotypeMediaId: string | null;
  readonly entete: Bilingue | null;
  readonly pied: Bilingue | null;
  readonly mentions: readonly Bilingue[];
  readonly couleurs: CouleursModele;
  readonly policeTitre: PoliceModele | null;
  readonly policeTexte: PoliceModele | null;
  readonly langue: Langue;
  readonly format: FormatPage;
  readonly actif: boolean;
  readonly texteCertificat: Bilingue | null;
  readonly signataire: { readonly nom: string; readonly qualite?: string } | null;
  readonly lieuEmission: string | null;
  readonly prixTtc: boolean;
  readonly logoEtiquettes: boolean;
  readonly modifieLe: string;
}

export interface CorpsMiseAJourModele {
  nom?: string;
  logotypeMediaId?: string | null;
  entete?: { fr: string; en?: string };
  pied?: { fr: string; en?: string };
  mentions?: { fr: string; en?: string }[];
  couleurs?: Partial<CouleursModele>;
  policeTitre?: PoliceModele;
  policeTexte?: PoliceModele;
  langue?: Langue;
  format?: FormatPage;
  actif?: boolean;
  signataire?: { nom: string; qualite?: string } | null;
  lieuEmission?: string | null;
  logoEtiquettes?: boolean;
}

// ── Export comptable (17.10) et liste de prix (17.11) ───────────────────────

export interface OptionsExportVentes {
  annee?: number;
  format?: 'csv' | 'json';
}

export interface EntreeListeDePrix {
  readonly oeuvreId: string;
  readonly artiste: string;
  readonly titre: string;
  readonly annee: string | null;
  readonly technique: string | null;
  readonly dimensions: string | null;
  readonly numeroInventaire: string;
  readonly prixCentimes: number | null;
  readonly devise: Devise;
  readonly regimeTva: RegimeTva;
  readonly statut: string;
}

export interface ReponseListeDePrix {
  readonly titre: string | null;
  readonly sousTitre: string | null;
  readonly langue: Langue;
  readonly entrees: readonly EntreeListeDePrix[];
  readonly total: number;
}

function requeteFiltresFactures(filtres: FiltresFactures): string {
  const params = new URLSearchParams();
  if (filtres.etat && filtres.etat.length > 0) params.set('etat', filtres.etat.join(','));
  if (filtres.type) params.set('type', filtres.type);
  if (filtres.contactId) params.set('contactId', filtres.contactId);
  if (filtres.annee !== undefined) params.set('annee', String(filtres.annee));
  if (filtres.du) params.set('du', filtres.du);
  if (filtres.au) params.set('au', filtres.au);
  if (filtres.impayees) params.set('impayees', 'true');
  if (filtres.q) params.set('q', filtres.q);
  params.set('tri', filtres.tri ?? 'recent');
  params.set('page', String(filtres.page ?? 1));
  params.set('parPage', String(filtres.parPage ?? 50));
  return params.toString();
}

export const ventesApi = {
  // ── Factures et avoirs ──────────────────────────────────────────────────
  lister: (galerie: string, filtres: FiltresFactures = {}, appel?: AppelApi) =>
    api.get<ReponseListeFactures>(
      `/g/${encodeURIComponent(galerie)}/ventes/factures?${requeteFiltresFactures(filtres)}`,
      { galerie, ...appel },
    ),
  obtenir: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<FicheFacture>(`/g/${encodeURIComponent(galerie)}/ventes/factures/${id}`, {
      galerie,
      ...appel,
    }),
  creer: (galerie: string, corps: CorpsFacture, appel?: AppelApi) =>
    api.post<{ facture: FacturePresentee }>(
      `/g/${encodeURIComponent(galerie)}/ventes/factures`,
      corps,
      { galerie, ...appel },
    ),
  modifier: (galerie: string, id: string, corps: CorpsMiseAJourFacture, appel?: AppelApi) =>
    api.patch<{ facture: FacturePresentee }>(
      `/g/${encodeURIComponent(galerie)}/ventes/factures/${id}`,
      corps,
      { galerie, ...appel },
    ),
  emettre: (galerie: string, id: string, corps: CorpsEmission, appel?: AppelApi) =>
    api.post<ReponseEmission>(
      `/g/${encodeURIComponent(galerie)}/ventes/factures/${id}/emettre`,
      corps,
      { galerie, ...appel },
    ),
  creerAvoir: (galerie: string, id: string, corps: CorpsAvoir = {}, appel?: AppelApi) =>
    api.post<ReponseAvoir>(`/g/${encodeURIComponent(galerie)}/ventes/factures/${id}/avoir`, corps, {
      galerie,
      ...appel,
    }),
  urlPdf: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<ReponsePdfFacture>(`/g/${encodeURIComponent(galerie)}/ventes/factures/${id}/pdf`, {
      galerie,
      ...appel,
    }),
  /** Le XML Factur-X, téléchargé par le client (compte attendu, signal). */
  facturX: (galerie: string, id: string, appel?: AppelApi) =>
    requeteTelechargement(`/g/${encodeURIComponent(galerie)}/ventes/factures/${id}/facturx.xml`, {
      galerie,
      ...appel,
    }),

  // ── Règlements ───────────────────────────────────────────────────────────
  ajouterReglement: (galerie: string, factureId: string, corps: CorpsReglement, appel?: AppelApi) =>
    api.post<ReponseReglement>(
      `/g/${encodeURIComponent(galerie)}/ventes/factures/${factureId}/reglements`,
      corps,
      { galerie, ...appel },
    ),
  supprimerReglement: (galerie: string, factureId: string, reglementId: string, appel?: AppelApi) =>
    requeteApi<ReponseSuppressionReglement>(
      `/g/${encodeURIComponent(galerie)}/ventes/factures/${factureId}/reglements/${reglementId}`,
      { method: 'DELETE', galerie, ...appel },
    ),
  journalReglements: (
    galerie: string,
    filtres: FiltresJournalReglements = {},
    appel?: AppelApi,
  ) => {
    const params = new URLSearchParams();
    if (filtres.du) params.set('du', filtres.du);
    if (filtres.au) params.set('au', filtres.au);
    if (filtres.mode) params.set('mode', filtres.mode);
    if (filtres.factureId) params.set('factureId', filtres.factureId);
    params.set('page', String(filtres.page ?? 1));
    params.set('parPage', String(filtres.parPage ?? 50));
    return api.get<ReponseJournalReglements>(
      `/g/${encodeURIComponent(galerie)}/ventes/reglements?${params.toString()}`,
      { galerie, ...appel },
    );
  },

  // ── Profil fiscal ────────────────────────────────────────────────────────
  profilFiscal: (galerie: string, appel?: AppelApi) =>
    api.get<{ profilFiscal: ProfilFiscal }>(
      `/g/${encodeURIComponent(galerie)}/ventes/profil-fiscal`,
      { galerie, ...appel },
    ),
  modifierProfilFiscal: (galerie: string, corps: CorpsMiseAJourProfil, appel?: AppelApi) =>
    api.patch<{ profilFiscal: ProfilFiscal }>(
      `/g/${encodeURIComponent(galerie)}/ventes/profil-fiscal`,
      corps,
      { galerie, ...appel },
    ),

  // ── Modèles de documents ────────────────────────────────────────────────
  modeles: (galerie: string, appel?: AppelApi) =>
    api.get<{ modeles: readonly ModeleDocument[] }>(
      `/g/${encodeURIComponent(galerie)}/ventes/modeles`,
      { galerie, ...appel },
    ),
  modele: (galerie: string, cle: CleModele, appel?: AppelApi) =>
    api.get<{ modele: ModeleDocument }>(`/g/${encodeURIComponent(galerie)}/ventes/modeles/${cle}`, {
      galerie,
      ...appel,
    }),
  modifierModele: (
    galerie: string,
    cle: CleModele,
    corps: CorpsMiseAJourModele,
    appel?: AppelApi,
  ) =>
    api.patch<{ modele: ModeleDocument }>(
      `/g/${encodeURIComponent(galerie)}/ventes/modeles/${cle}`,
      corps,
      { galerie, ...appel },
    ),

  // ── Export comptable et liste de prix ───────────────────────────────────
  exporter: (galerie: string, options: OptionsExportVentes = {}, appel?: AppelApi) => {
    const params = new URLSearchParams();
    if (options.annee !== undefined) params.set('annee', String(options.annee));
    params.set('format', options.format ?? 'csv');
    return requeteTelechargement(
      `/g/${encodeURIComponent(galerie)}/ventes/export?${params.toString()}`,
      { galerie, ...appel },
    );
  },
  listeDePrix: (
    galerie: string,
    options: { oeuvres?: string[]; exposition?: string; langue?: Langue },
    appel?: AppelApi,
  ) => {
    const params = new URLSearchParams();
    if (options.exposition) params.set('exposition', options.exposition);
    else if (options.oeuvres) params.set('oeuvres', options.oeuvres.join(','));
    if (options.langue) params.set('langue', options.langue);
    return api.get<ReponseListeDePrix>(
      `/g/${encodeURIComponent(galerie)}/ventes/liste-de-prix?${params.toString()}`,
      { galerie, ...appel },
    );
  },
};

// ── Hooks React Query ────────────────────────────────────────────────────────

const cleListe = (galerie: string, filtres: FiltresFactures) =>
  cleMetier(galerie, 'ventes', 'factures', 'liste', filtres);
const cleFiche = (galerie: string, id: string) =>
  cleMetier(galerie, 'ventes', 'factures', 'fiche', id);
const cleProfil = (galerie: string) => cleMetier(galerie, 'ventes', 'profil-fiscal');
const cleModeles = (galerie: string) => cleMetier(galerie, 'ventes', 'modeles');
const cleModele = (galerie: string, cle: CleModele) => cleMetier(galerie, 'ventes', 'modele', cle);

export function useFactures(
  galerie: string,
  filtres: FiltresFactures = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    enabled: options.enabled ?? true,
    queryKey: cleListe(galerie, filtres),
    queryFn: (contexte) => ventesApi.lister(galerie, filtres, appelDeRequete(contexte)),
    placeholderData: precedentDansLaPortee(galerie),
  });
}

export function useFacture(galerie: string, id: string | null) {
  return useQuery({
    queryKey: cleFiche(galerie, id ?? ''),
    queryFn: (contexte) => ventesApi.obtenir(galerie, id ?? '', appelDeRequete(contexte)),
    enabled: id !== null,
    // Le PDF et le XML Factur-X sont rendus par le worker APRÈS l'émission
    // (`invoice:document`, D9) : sans ce rafraîchissement, les boutons de
    // téléchargement n'apparaissaient qu'en rechargeant la page. On relit
    // toutes les deux secondes, une minute au plus — un rendu en lettre morte
    // ne doit pas faire interroger l'API indéfiniment.
    refetchInterval: (requete) => {
      const facture = requete.state.data?.facture;
      const enAttente =
        facture !== undefined &&
        facture.etat !== 'brouillon' &&
        (!facture.pdfDisponible || !facture.facturXDisponible);
      return enAttente && requete.state.dataUpdateCount < 30 ? 2000 : false;
    },
  });
}

function invaliderFactures(client: ReturnType<typeof useQueryClient>, galerie: string): void {
  void client.invalidateQueries({ queryKey: cleMetier(galerie, 'ventes', 'factures', 'liste') });
}

export function useCreerFacture(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: CorpsFacture, appel) => ventesApi.creer(galerie, corps, appel),
      onSuccess: () => invaliderFactures(client, galerie),
    }),
  );
}

export function useModifierFacture(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, corps }: { id: string; corps: CorpsMiseAJourFacture }, appel) =>
        ventesApi.modifier(galerie, id, corps, appel),
      onSuccess: (_donnees, variables) => {
        invaliderFactures(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
      },
    }),
  );
}

export function useEmettreFacture(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, corps }: { id: string; corps: CorpsEmission }, appel) =>
        ventesApi.emettre(galerie, id, corps, appel),
      onSuccess: (_donnees, variables) => {
        invaliderFactures(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
      },
    }),
  );
}

export function useCreerAvoir(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, corps }: { id: string; corps?: CorpsAvoir }, appel) =>
        ventesApi.creerAvoir(galerie, id, corps, appel),
      onSuccess: (_donnees, variables) => {
        invaliderFactures(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
      },
    }),
  );
}

export function useAjouterReglement(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ factureId, corps }: { factureId: string; corps: CorpsReglement }, appel) =>
        ventesApi.ajouterReglement(galerie, factureId, corps, appel),
      onSuccess: (_donnees, variables) => {
        invaliderFactures(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.factureId) });
      },
    }),
  );
}

export function useSupprimerReglement(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ factureId, reglementId }: { factureId: string; reglementId: string }, appel) =>
        ventesApi.supprimerReglement(galerie, factureId, reglementId, appel),
      onSuccess: (_donnees, variables) => {
        invaliderFactures(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.factureId) });
      },
    }),
  );
}

export function useProfilFiscal(galerie: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: cleProfil(galerie),
    queryFn: (contexte) => ventesApi.profilFiscal(galerie, appelDeRequete(contexte)),
    enabled: options.enabled ?? true,
  });
}

export function useModifierProfilFiscal(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: CorpsMiseAJourProfil, appel) =>
        ventesApi.modifierProfilFiscal(galerie, corps, appel),
      onSuccess: () => {
        void client.invalidateQueries({ queryKey: cleProfil(galerie) });
      },
    }),
  );
}

export function useModelesVentes(galerie: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: cleModeles(galerie),
    queryFn: (contexte) => ventesApi.modeles(galerie, appelDeRequete(contexte)),
    enabled: options.enabled ?? true,
  });
}

export function useModifierModeleVentes(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ cle, corps }: { cle: CleModele; corps: CorpsMiseAJourModele }, appel) =>
        ventesApi.modifierModele(galerie, cle, corps, appel),
      onSuccess: (_donnees, variables) => {
        void client.invalidateQueries({ queryKey: cleModeles(galerie) });
        void client.invalidateQueries({ queryKey: cleModele(galerie, variables.cle) });
      },
    }),
  );
}

/**
 * L'URL signée de 5 minutes du PDF (§ 17.6) : interrogée seulement quand la
 * fiche l'affiche (panneau d'aperçu), jamais préchargée pour toute la liste.
 */
export function usePdfFacture(
  galerie: string,
  id: string | null,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: cleMetier(galerie, 'ventes', 'factures', 'pdf', id ?? ''),
    queryFn: (contexte) => ventesApi.urlPdf(galerie, id ?? '', appelDeRequete(contexte)),
    enabled: (options.enabled ?? true) && id !== null,
    staleTime: 60_000,
  });
}
